import { useEffect, useMemo, useState, useCallback } from 'react'
import Parse from 'parse'
import { useForm } from 'react-hook-form'
import styled, { createGlobalStyle } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { logEvent } from 'firebase/analytics'
import { marketplace, money } from '@sellpy/commons'
import { Button } from '@sellpy/design-system-react-web'
import { useDispatch } from 'react-redux'
import AdyenPolicy from '../../checkout/components/AdyenPolicy.jsx'
import { ADYEN_PAYMENT_OPTIONS, getRegion } from '../../../common/region/region.js'
import { getAppAnalytics } from '../../../../../app-payments/analytics/analytics.js'
import { getAnalyticsFormattedItem } from '../../../../../app-payments/analytics/formatAnalyticsData.js'
import { fetchUserCredits } from '../../../common/cart/actions.js'
import CreditPayoutModal from '../../payOuts/credit/CreditPayoutModal.jsx'
import DataConsent from '../dataConsent/DataConsent.jsx'
import { getTotalBalanceForUser } from '../../../common/balance/actions.js'
import {
  useKlarnaCheckout,
  useKlarnaPaymentMethods
} from '../../checkoutV2/hooks/klarnaCheckout.js'
import {
  getDetailedType,
  useAdyenCheckout,
  useAdyenPaymentMethods
} from '../../checkoutV2/hooks/adyenCheckout.js'
import { normalizeDataConsentValues } from '../../utils/formHelpers.js'
import { analyticsEnableEmailMarketing } from '../../../common/analytics/actions.js'
import { getKlarnaPaymentCategory } from '../payment/utils.js'
import {
  KLARNA_PAYMENT_OPTIONS,
  nothingToPayWithAdyen,
  replaceAdyenKlarnaOptions
} from './components/utils.js'
import ErrorHandler, { ERRORS } from './components/ErrorHandler.jsx'
import { useMaxCreditAmount } from './hooks/useMaxCreditAmount.js'
import CreditsV2 from './components/CreditsV2.jsx'
import KlarnaWidgets from './components/KlarnaWidgets.jsx'
import { useIsAdyenReturn } from './hooks/useIsAdyenReturn.js'
import { KlarnaRadioButton } from './components/KlarnaRadioButton.jsx'
import { AdyenRadioButton } from './components/AdyenRadioButton.jsx'

const BottomWrapper = styled.div`
  background-color: ${({ theme }) => theme.color.grey.shade8};
  padding: 16px;
`

const TopWrapper = styled.div`
  padding: 16px;
`

const ApplePayButtonOverrides = createGlobalStyle`
  .adyen-checkout__applepay__button {
    width: 100% !important;
    max-width: 100% !important;
    display: block !important;
  }

  .adyen-checkout__applepay__button::after {
    width: 100% !important;
    max-width: 100% !important;
    display: block !important;
  }
`

// const getComponentKey = (component) => component?.identifier ?? component?._id
// Note: We are not using `_id` for Adyen components since those change each time a new session is created,
// and we need to make a new session each time fast handling is chosen
const getComponentKey = (component) => component?.identifier ?? component?.accessibleName

const KLARNA_PAYMENT_OPTIONS_LIST = Object.values(KLARNA_PAYMENT_OPTIONS)

const isKlarnaPaymentIdentifier = (identifier) => KLARNA_PAYMENT_OPTIONS_LIST.includes(identifier)

const PaymentOptions = ({
  allPaymentOptions,
  paymentRefs,
  errors,
  disabled,
  setSelectedPaymentKey,
  selectedPaymentKey,
  hideUnselected,
  clearErrors
}) => {
  if (!allPaymentOptions?.length) {
    return <div />
  }

  return allPaymentOptions.map((component) => {
    const componentKey = getComponentKey(component)
    if (isKlarnaPaymentIdentifier(component.identifier)) {
      const isSelected = selectedPaymentKey === componentKey
      return (
        <KlarnaRadioButton
          key={componentKey}
          component={component}
          isSelected={isSelected}
          hideUnselected={hideUnselected}
          disabled={disabled}
          erroneous={Boolean(errors.paymentOptions)}
          paymentRefs={paymentRefs}
          onSelect={() => {
            setSelectedPaymentKey(componentKey)
            clearErrors()
          }}
        />
      )
    }
    if (ADYEN_PAYMENT_OPTIONS.includes(getDetailedType(component))) {
      const isSelected = selectedPaymentKey === componentKey

      return (
        <AdyenRadioButton
          key={componentKey}
          component={component}
          isSelected={isSelected}
          hideUnselected={hideUnselected}
          disabled={disabled}
          erroneous={Boolean(errors.paymentOptions)}
          paymentRefs={paymentRefs}
          onSelect={() => {
            setSelectedPaymentKey(componentKey)
            clearErrors()
          }}
        />
      )
    }
  })
}

/**
 * @param {function} adyenCreateSession - Expect a cloud function that uses the createSessionCheckoutAdyenWrapper, this will fetch a session object that SessionPayment uses to initate the payment sessions.
 * @param {function} adyenCommenceSession - Expect a cloud function that uses the runSessionCheckoutAdyenWrapper,
 * @param {function} adyenCommenceSessionAnalyticsCallback - Analytics that should be performed in conjuntion with adyenCommenceSession. This should be passed separately to avoid messing up the dependency array of `useAdyenCheckout`, which could inadvertently result in multiple Adyen sessions being created.
 * this will run when a customer click on the pay button but an error will break the payment flow. The function will get adyenPaymentData, creditAmount and dataUsageConsent from SessionPayment
 * @param {function} createKlarnaOrder - Expect a cloud function that will run when a customer click on the pay button with a Klarna payment option,
 * but an error will break the payment flow. The function will get creditAmount, klarnaPaymentMethod, dataUsageConsent, authorizationToken, sessionId from SessionPayment
 * @param {object} totalAmount - The total amount (in subUnit) that the customer should pay.
 * @param {function} amountElement - A react element/component that visulize the the amount the customer is will pay. The element will get adyenAmount and creditsAmount from SessionPayment depending on what the customer has selected.
 * @param {boolean} [enableCredits=true] - Define if credits should be shown for the customer, this doesn't stop the customer from using credits, a validation in the backend needs to be set as well.
 * @param {array} [userPermissions] - An array of the user permissions that the user can or need to consent to.
 * @param {array} [appPaymentReservationIds] - An array of the reservation ids that the customer is paying for. Used for tracking app events, and is only included if it's an item payment from the app.
 */
const SessionPayment = ({
  adyenCreateSession,
  adyenCommenceSession,
  adyenCommenceSessionAnalyticsCallback,
  klarnaOverride,
  createKlarnaOrder,
  createKlarnaSession,
  totalAmount,
  amountElement,
  enableCredits = true,
  userPermissions,
  isLoyaltyPayment = false,
  appPaymentReservationIds,
  regionOverride = getRegion(),
  checkoutType,
  paymentFromLastOrder,
  isAppCheckout = false,
  noPadding = false,
  hideUnselected
}) => {
  const { t } = useTranslation(['adyenSessionPayment', 'common', 'checkout'])
  const [creditPayoutModalOpen, setCreditPayoutModalOpen] = useState(false)
  const dispatch = useDispatch()
  const isReturnFromAdyen = useIsAdyenReturn()

  const { klarnaPaymentMethodRefs } = useKlarnaPaymentMethods()
  const { adyenPaymentMethodRefs } = useAdyenPaymentMethods()

  const paymentRefs = useMemo(
    () => ({ ...adyenPaymentMethodRefs, ...klarnaPaymentMethodRefs }),
    [adyenPaymentMethodRefs, klarnaPaymentMethodRefs]
  )

  const {
    control,
    handleSubmit,
    setError,
    trigger,
    clearErrors,
    getValues,
    formState: { errors, isDirty }
  } = useForm({})

  const maxCreditAmount = useMaxCreditAmount(totalAmount)
  const [useCredits, setUseCredits] = useState(false)
  const [selectedPaymentKey, setSelectedPaymentKey] = useState()

  const creditAmount = useCredits ? money.toBaseUnit(maxCreditAmount).amount : 0

  const nothingToPayWithMoney = money.toBaseUnit(totalAmount).amount - creditAmount <= 0

  const getDataUsageConsent = useCallback(async () => {
    const formDataIsValid = await trigger()
    if (!formDataIsValid) return new Error('invalidFormData')
    return normalizeDataConsentValues(getValues()).dataUsageConsent
  }, [trigger, getValues])

  const {
    adyenCheckout: checkout,
    loading: adyenLoading,
    error: adyenError,
    paymentId,
    commencePayment
  } = useAdyenCheckout({
    createSessionCallback: adyenCreateSession,
    commenceSessionCallback: adyenCommenceSession,
    adyenCommenceSessionAnalyticsCallback,
    isLoyaltyCheckout: isLoyaltyPayment,
    creditAmount,
    totalPrice: totalAmount,
    getDataUsageConsent,
    isAppCheckout
  })

  const {
    klarnaPaymentOptions,
    createOrder,
    loading: klarnaLoading,
    error: klarnaError
  } = useKlarnaCheckout({
    creditAmount,
    nothingToPayWithMoney,
    shouldUseKlarna: klarnaOverride,
    createKlarnaOrder,
    createKlarnaSession
  })
  const allPaymentOptions = useMemo(
    () =>
      checkout?.components &&
      replaceAdyenKlarnaOptions({
        adyenPaymentOptions: checkout.components,
        klarnaPaymentOptions
      }),
    [klarnaPaymentOptions, checkout?.components]
  )

  useEffect(() => {
    if (!allPaymentOptions || isDirty || selectedPaymentKey) {
      return
    }
    const lastPaymentType =
      paymentFromLastOrder?.adyenPaymentMethod?.type ??
      getKlarnaPaymentCategory(paymentFromLastOrder?.klarnaPaymentMethod.type)

    const preselectedComponent =
      lastPaymentType &&
      allPaymentOptions.find(
        ({ data, identifier }) => (data?.paymentMethod?.type ?? identifier) === lastPaymentType
      )

    setSelectedPaymentKey(getComponentKey(preselectedComponent ?? allPaymentOptions[0]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentFromLastOrder, checkout, allPaymentOptions, isDirty])

  useEffect(() => {
    if (adyenError) {
      setError(adyenError.type, adyenError.details)
    } else {
      clearErrors()
    }
  }, [adyenError, clearErrors, setError])

  useEffect(() => {
    if (klarnaError) {
      setError(klarnaError.type, klarnaError.details)
    } else {
      clearErrors()
    }
  }, [klarnaError, clearErrors, setError])

  const onSubmit = async ({ dataUsageConsent }) => {
    try {
      const formDataIsValid = await trigger()
      if (!formDataIsValid) throw new Error('invalidFormData')
    } catch (error) {
      setError(ERRORS.INVALID_FORM_DATA, error)
      return
    }
    dataUsageConsent?.emailMarketing && analyticsEnableEmailMarketing()
    const matchingAdyenComponent = checkout?.components?.find(
      (c) => getComponentKey(c) === selectedPaymentKey
    )
    isKlarnaPaymentIdentifier(selectedPaymentKey)
      ? createOrder({ dataUsageConsent, paymentOptions: selectedPaymentKey })
      : commencePayment({ dataUsageConsent, paymentOptions: matchingAdyenComponent?._id })
  }

  // analytics
  useEffect(() => {
    if (!selectedPaymentKey || !checkout || !appPaymentReservationIds) return

    const selectedComponent = checkout.components.find(
      (comp) => getComponentKey(comp) === selectedPaymentKey
    )
    if (!selectedComponent) return

    const selectedType = isKlarnaPaymentIdentifier(selectedPaymentKey)
      ? selectedPaymentKey
      : getDetailedType(selectedComponent)

    const fetchDataAndLogAnalyticsEvent = async () => {
      const reservations = await new Parse.Query('ItemReservation')
        .containedIn('objectId', appPaymentReservationIds)
        .find()
      const itemIds = reservations.map((reservation) => reservation.get('item').id)

      logEvent(getAppAnalytics(), 'add_payment_info', {
        payment_type: selectedType,
        items: await Promise.all(
          itemIds.map((itemId) => getAnalyticsFormattedItem({ item: { objectId: itemId } }))
        )
      })
    }
    fetchDataAndLogAnalyticsEvent()
  }, [selectedPaymentKey, checkout, appPaymentReservationIds])

  /**
   * If `window.paymentData` exists then we know this payment is initiated from Sellphone,
   * and we should open the credit payout in the app instead of the link.
   */
  const windowPaymentDataExists = Boolean(window.paymentData)

  useEffect(() => {
    if (windowPaymentDataExists) {
      window.fetchUserCredits = () => {
        dispatch(fetchUserCredits(getRegion()))
        dispatch(getTotalBalanceForUser())
      }

      return () => {
        delete window.fetchUserCredits
      }
    }
  }, [windowPaymentDataExists, dispatch])

  useEffect(() => {
    if (!creditPayoutModalOpen) dispatch(fetchUserCredits(getRegion()))
  }, [creditPayoutModalOpen, dispatch])

  const handleOnClick = () =>
    windowPaymentDataExists
      ? window.ReactNativeWebView.postMessage('openCreditPayout')
      : setCreditPayoutModalOpen(true)

  const selectedPaymentComponent = allPaymentOptions?.find(
    (c) => getComponentKey(c) === selectedPaymentKey
  )
  const isApplePaySelected = selectedPaymentComponent?._id?.startsWith('applepay')
  const isPayPalSelected = selectedPaymentComponent?._id?.startsWith('paypal')
  return (
    <div>
      <TopWrapper style={{ padding: noPadding ? 0 : undefined }}>
        {enableCredits && (
          <>
            {!windowPaymentDataExists && (
              <CreditPayoutModal
                isOpen={creditPayoutModalOpen}
                setIsOpen={setCreditPayoutModalOpen}
                checkoutType={checkoutType}
              />
            )}
            <CreditsV2
              totalAmount={totalAmount}
              handleOnClick={handleOnClick}
              setUseCredits={setUseCredits}
              loading={adyenLoading || klarnaLoading}
              checkoutType={checkoutType}
              onChange={() => {
                // Resets the component
                setSelectedPaymentKey(null)
                // "Force-re-initialize" the paypal component to make it use the latest session
                checkout?.components.find((component) => component.type === 'paypal')?.unmount()
                checkout?.components.find((component) => component.type === 'applepay')?.unmount()
              }}
            />
          </>
        )}
        {!nothingToPayWithAdyen(checkout) && (
          <PaymentOptions
            allPaymentOptions={allPaymentOptions}
            key={paymentId}
            errors={errors}
            paymentRefs={paymentRefs}
            setSelectedPaymentKey={setSelectedPaymentKey}
            selectedPaymentKey={selectedPaymentKey}
            disabled={adyenLoading || isReturnFromAdyen || klarnaLoading}
            hideUnselected={hideUnselected}
            clearErrors={clearErrors}
          />
        )}
      </TopWrapper>
      <BottomWrapper>
        <amountElement.type
          {...amountElement.props}
          adyenAmount={{
            currency: checkout?.options?.amount?.currency,
            amount: checkout?.options?.amount?.value
          }}
          adyenLoading={adyenLoading}
          creditAmount={money.toSubUnit({
            currency: marketplace.CURRENCY[getRegion()],
            amount: creditAmount
          })}
        />
        <DataConsent
          control={control}
          permissions={userPermissions}
          showCheckoutPolicies
          regionOverride={regionOverride}
        />
        <ErrorHandler errors={errors} clearErrors={clearErrors} />
        <div
          ref={adyenPaymentMethodRefs.paypal}
          style={{ display: isPayPalSelected ? 'inherit' : 'none' }}
        />
        <ApplePayButtonOverrides />
        <div
          ref={adyenPaymentMethodRefs.applepay}
          style={{
            display: isApplePaySelected ? 'inherit' : 'none',
            paddingBottom: '16px',
            width: '100%'
          }}
        />
        <Button
          label={t('adyenSessionPayment:payButton')}
          loading={adyenLoading || klarnaLoading}
          fullWidth
          style={{
            margin: '0 0 8px 0',
            display: isPayPalSelected || isApplePaySelected ? 'none' : 'inherit'
          }}
          onClick={handleSubmit(onSubmit)}
          disabled={Object.keys(errors).length > 0 || adyenLoading || klarnaLoading}
        />
        <KlarnaWidgets selectedPaymentOption={selectedPaymentKey} />
        {!isKlarnaPaymentIdentifier(selectedPaymentKey) && <AdyenPolicy />}
      </BottomWrapper>
    </div>
  )
}

export default SessionPayment
